import * as XLSX from "xlsx";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/authContext";
import { getTimeReport, TimeReportRow } from "@/lib/api";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Download, Loader2, ShieldAlert, ChevronUp, ChevronDown, Printer } from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subWeeks, subMonths, startOfYear, endOfYear } from "date-fns";

type SortKey = "userName" | "totalMinutes" | "billableMinutes" | "billablePercent" | "entriesCount";
type SortDir = "asc" | "desc";

export default function ReportTime() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "admin";

  const today = new Date();
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("userName");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    enabled: isManager,
  });

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["reports-time", dateFrom, dateTo, selectedUserId],
    queryFn: () =>
      getTimeReport({
        dateFrom,
        dateTo,
        userId: selectedUserId !== "all" ? selectedUserId : undefined,
      }),
    enabled: isManager && !!dateFrom && !!dateTo,
  });

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = typeof av === "string" ? av.localeCompare(bv as string) : (av as number) - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const totals = useMemo(() => ({
    totalMinutes: rows.reduce((s, r) => s + r.totalMinutes, 0),
    billableMinutes: rows.reduce((s, r) => s + r.billableMinutes, 0),
    entriesCount: rows.reduce((s, r) => s + r.entriesCount, 0),
  }), [rows]);

  const totalBillPct = totals.totalMinutes > 0
    ? Math.round((totals.billableMinutes / totals.totalMinutes) * 100)
    : 0;

  const chartData = rows.map(r => ({
    name: r.userName.split(" ")[0],
    billable: Math.round((r.billableMinutes / 60) * 100) / 100,
    nonBillable: Math.round(((r.totalMinutes - r.billableMinutes) / 60) * 100) / 100,
  }));

  function handleSort(key: SortKey) {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (col !== sortKey) return <ChevronUp className="w-3 h-3 opacity-20 inline ml-1" />;
    return sortDir === "asc"
      ? <ChevronUp className="w-3 h-3 inline ml-1 text-primary" />
      : <ChevronDown className="w-3 h-3 inline ml-1 text-primary" />;
  }

  function exportCSV() {
    const header = ["Name", "Total Hours", "Billable Hours", "Billable %", "Entries"];
    const dataRows = sorted.map(r => [
      r.userName,
      parseFloat((r.totalMinutes / 60).toFixed(2)),
      parseFloat((r.billableMinutes / 60).toFixed(2)),
      r.billablePercent,
      r.entriesCount,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    ws["!cols"] = [{ wch: 25 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 10 }];
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Time Utilization");
    XLSX.writeFile(wb, `time-report-${dateFrom}-${dateTo}.xlsx`);
  }

  if (!isManager) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-3xl font-heading font-bold">Time Utilization Report</h1>
          <Card className="border-dashed border-2 border-destructive/50">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShieldAlert className="w-12 h-12 mb-4 text-destructive/50" />
              <p className="text-lg font-medium">Access Denied</p>
              <p className="text-sm">You need manager or administrator privileges to access reports.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Time Utilization Report</h1>
          <p className="text-muted-foreground">Billable vs non-billable hours by staff member</p>
        </div>

        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {[
                { label: "This Week", from: startOfWeek(today, { weekStartsOn: 1 }), to: endOfWeek(today, { weekStartsOn: 1 }) },
                { label: "Last Week", from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }) },
                { label: "This Month", from: startOfMonth(today), to: endOfMonth(today) },
                { label: "Last Month", from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)) },
                { label: "This Year", from: startOfYear(today), to: endOfYear(today) },
              ].map(({ label, from, to }) => (
                <Button
                  key={label}
                  variant="outline"
                  size="sm"
                  onClick={() => { setDateFrom(format(from, "yyyy-MM-dd")); setDateTo(format(to, "yyyy-MM-dd")); }}
                >
                  {label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground font-medium">From</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground font-medium">To</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground font-medium">Staff Member</span>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {(users ?? []).map((u: any) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => window.print()} disabled={rows.length === 0}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" onClick={exportCSV} disabled={rows.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center text-destructive">
              Failed to load report data.
            </CardContent>
          </Card>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No time entries found for the selected range.
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30 text-muted-foreground text-left">
                      <th className="px-4 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort("userName")}>
                        Staff Name <SortIcon col="userName" />
                      </th>
                      <th className="px-4 py-3 font-medium text-right cursor-pointer select-none" onClick={() => handleSort("totalMinutes")}>
                        Total Hours <SortIcon col="totalMinutes" />
                      </th>
                      <th className="px-4 py-3 font-medium text-right cursor-pointer select-none" onClick={() => handleSort("billableMinutes")}>
                        Billable Hours <SortIcon col="billableMinutes" />
                      </th>
                      <th className="px-4 py-3 font-medium text-right cursor-pointer select-none" onClick={() => handleSort("billablePercent")}>
                        Billable % <SortIcon col="billablePercent" />
                      </th>
                      <th className="px-4 py-3 font-medium text-right cursor-pointer select-none" onClick={() => handleSort("entriesCount")}>
                        Entries <SortIcon col="entriesCount" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sorted.map((r: TimeReportRow) => {
                      const pctColor =
                        r.billablePercent >= 80
                          ? "text-emerald-600"
                          : r.billablePercent >= 60
                          ? "text-amber-600"
                          : "text-red-600";
                      return (
                        <tr key={r.userId} className="hover:bg-muted/20">
                          <td className="px-4 py-3 font-medium">{r.userName}</td>
                          <td className="px-4 py-3 text-right font-mono">{(r.totalMinutes / 60).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-mono">{(r.billableMinutes / 60).toFixed(2)}</td>
                          <td className={`px-4 py-3 text-right font-mono font-semibold ${pctColor}`}>
                            {r.billablePercent}%
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground">{r.entriesCount}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/40 font-semibold">
                      <td className="px-4 py-3">Total</td>
                      <td className="px-4 py-3 text-right font-mono">{(totals.totalMinutes / 60).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono">{(totals.billableMinutes / 60).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono">{totalBillPct}%</td>
                      <td className="px-4 py-3 text-right">{totals.entriesCount}</td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>

            {chartData.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Billable vs Non-Billable Hours</CardTitle>
                </CardHeader>
                <CardContent className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} unit="h" />
                      <Tooltip formatter={(v: number) => [`${v.toFixed(2)}h`]} />
                      <Legend />
                      <Bar dataKey="billable" name="Billable" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="nonBillable" name="Non-Billable" fill="hsl(var(--chart-5))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
